import type { SupportedIcon } from 'corso-admin-ui';
import { PrimaryEntity, Sidekick } from 'corso-types';
import { match, P } from 'ts-pattern';

/**
 * Every word Sidekick shows in the merchant app, in one place to review.
 * Tool step labels ("Looking up the claim") are shared with the MCP server: `mcpToolDisplay` in corso-types.
 */
export const sidekickCopy = {
  releaseBanner: {
    message: 'Our new AI experience is here.',
    cta: 'Let’s chat!',
    prompt: 'What can I do with the **Corso Sidekick**?',
  },
  greeting: {
    morning: 'Good morning',
    afternoon: 'Good afternoon',
    evening: 'Good evening',
    question: 'How can I help?',
  },
  header: {
    newChat: 'New chat',
    rename: 'Rename chat',
    close: 'Close Corso AI',
    noConversations: 'No prior conversations.',
    resize: 'Resize Corso AI',
  },
  conversationGroup: {
    today: 'Today',
    yesterday: 'Yesterday',
    earlier: 'Earlier',
  },
  rename: {
    title: 'Rename chat',
    name: 'Name',
    save: 'Save',
  },
  composer: {
    placeholder: 'Ask anything...',
    send: 'Send',
    stop: 'Stop',
    dismissPage: 'Remove page context',
  },
  thinking: 'Thinking...',
  message: {
    copy: 'Copy',
    copyLabel: 'Copy response',
    regenerate: 'Regenerate',
    regenerateLabel: 'Regenerate response',
    scrollToBottom: 'Scroll to bottom',
    failed: 'Sorry, something went wrong. Please try again.',
  },
  requestError: {
    rateLimited: 'That’s a lot of questions at once. Try again in a minute.',
    failed: 'Sorry, that didn’t go through.',
    retry: 'Retry',
  },
  feedback: {
    helpful: 'Helpful',
    toggleHelpful: 'Toggle Helpful',
    unhelpful: 'Unhelpful',
    toggleUnhelpful: 'Toggle Unhelpful',
    addComment: 'Add Comment',
    submitted: 'Feedback Submitted',
    title: 'Feedback',
    description: (sentiment: Sidekick.UserSentiment) =>
      `Thanks for sharing your feedback! What was ${sentiment} about this message? Your feedback will help us improve future responses.`,
    label: 'Feedback',
    submit: 'Submit',
  },
  queryResult: {
    title: 'Query results',
    showTable: 'Show table',
    hideTable: 'Hide table',
    more: (count: number) => `+${count} more`,
    yes: 'Yes',
    no: 'No',
  },
  portalCss: {
    proposal: { title: 'New portal look', status: 'Not saved' },
    saved: { title: 'Portal look saved', status: 'Live' },
    review: { title: 'Your portal', status: 'Live' },
    reverted: { title: 'Previous portal look restored', status: 'Live' },
    checkup: (issues: number) => ({
      title: 'Portal check-up',
      status: issues ? `${issues} to fix` : 'Healthy',
    }),
    finding: (issue: string, lines: number[]) =>
      lines.length
        ? `${issue} (line${lines.length > 1 ? 's' : ''} ${lines.slice(0, 5).join(', ')}${lines.length > 5 ? '…' : ''})`
        : issue,
    dependsOnSite:
      'Embedded, text on the page sits on your site’s background, so check it there too.',
    checksPass: (failing: number, total: number) =>
      failing
        ? `Readability: ${total - failing} of ${total} checks pass`
        : `Readability: all ${total} checks pass`,
    hardToRead: (pair: string, suggestedText?: string) =>
      `${pair} may be hard to read.${suggestedText ? ` ${suggestedText === '#ffffff' ? 'White' : 'Dark'} text would fix it.` : ''}`,
    unsupported: (request: string) =>
      `Not possible yet, so we passed it to our team: ${request}`,
    emailHeader:
      'Your brand color also changed the header of your customer emails.',
    sayToSave: 'Say “save” to apply it.',
    openPortal: 'Open portal',
    viewCss: 'View CSS',
    copyLabel: 'Copy CSS',
  },
} as const;

// the chip under the input: a record reads as its type and the number its page header shows
export const pageLabel = (
  page: Sidekick.PageContext,
  externalId: string | undefined,
) => {
  const number = externalId ? ` #${externalId}` : '';
  return match(page)
    .with(
      { kind: P.union(PrimaryEntity.Return, PrimaryEntity.Warranty) },
      ({ kind }) => `${kind}${number}`,
    )
    .with({ kind: PrimaryEntity.Shipping }, ({ id }) => `Shipping Claim #${id}`)
    .with({ kind: PrimaryEntity.Registration }, () => `Registration${number}`)
    .with({ kind: PrimaryEntity.Order }, () => 'Order')
    .with({ kind: 'Automation' }, () => 'Automation')
    .with(
      { kind: 'Settings' },
      ({ section }) => `${section.replaceAll('-', ' ')} settings`,
    )
    .exhaustive();
};

// the chip's icon: the one the app's nav uses for that kind of record
export const pageIcon = (page: Sidekick.PageContext) =>
  match(page)
    .returnType<SupportedIcon>()
    .with({ kind: PrimaryEntity.Return }, () => 'exchange')
    .with({ kind: PrimaryEntity.Warranty }, () => 'verified')
    .with({ kind: PrimaryEntity.Shipping }, () => 'productGroup')
    .with({ kind: PrimaryEntity.Registration }, () => 'registration')
    .with({ kind: PrimaryEntity.Order }, () => 'order')
    .with({ kind: 'Automation' }, () => 'automation')
    .with({ kind: 'Settings' }, () => 'settings')
    .exhaustive();

export type StarterPrompt = {
  prompt: string;
  // Staff can't read settings or analytics, so they're never offered a prompt they can't get answered
  adminOnly?: true;
};

// starter prompts for each kind of page; a new chat shows a few of them, rotating, so add freely
// returns and warranties are both claims, asked about the same way
const claimStarters: StarterPrompt[] = [
  { prompt: 'Explain how the money moved on this claim' },
  { prompt: 'Why is this claim in its current status?' },
  { prompt: 'Summarize what happened on this claim' },
  { prompt: 'Has this customer returned anything else?' },
  { prompt: 'Why is the customer sending this back?' },
  { prompt: 'How risky is this claim, and why?' },
  { prompt: 'What did the customer pay, and what are they owed?' },
];

export const starterPrompts = {
  [PrimaryEntity.Return]: claimStarters,
  [PrimaryEntity.Warranty]: claimStarters,
  [PrimaryEntity.Shipping]: [
    { prompt: 'Explain how this shipping claim was resolved' },
    { prompt: 'What did shipping protection cover here?' },
    { prompt: 'Has this customer filed other shipping claims?' },
  ],
  [PrimaryEntity.Registration]: [
    { prompt: 'Summarize this registration' },
    { prompt: 'What did this customer register?' },
    { prompt: 'Has this customer registered other products?' },
  ],
  [PrimaryEntity.Order]: [
    { prompt: 'Can this order still be returned?' },
    { prompt: 'What can each item be returned for, and until when?' },
    { prompt: 'What would happen if they returned an item as too small?' },
    { prompt: 'Which automations apply to this order?' },
    { prompt: 'Where is this order?' },
    { prompt: 'Has this order been returned or exchanged?' },
    { prompt: 'Is this order covered by shipping protection?' },
  ],
  Automation: [
    { prompt: 'Explain what this automation does' },
    { prompt: 'When does this automation run?' },
    { prompt: 'What would change if this automation were off?' },
    { prompt: 'Would this automation apply to a specific order?' },
  ],
  Settings: [
    { prompt: 'What do these settings do?' },
    { prompt: 'What are mine set to?' },
    { prompt: 'How do customers see this?' },
  ],
  // any page without something specific to point at
  Anywhere: [
    { prompt: 'What’s my return window?', adminOnly: true },
    { prompt: 'Show this week’s returns', adminOnly: true },
    { prompt: 'What are my top return reasons this month?', adminOnly: true },
    { prompt: 'Which products are returned most this month?', adminOnly: true },
    { prompt: 'What’s my return rate over the last 90 days?', adminOnly: true },
    {
      prompt:
        'How much revenue did exchanges and store credit keep this month?',
      adminOnly: true,
    },
    {
      prompt: 'How much did return labels cost me this month?',
      adminOnly: true,
    },
    { prompt: 'How long do returns take to resolve?', adminOnly: true },
    { prompt: 'Why are customers filing shipping claims?', adminOnly: true },
  ],
} satisfies Record<Sidekick.PageContext['kind'] | 'Anywhere', StarterPrompt[]>;
